/**
 * The Task sheet — `docs/activity.md` §8.2 (Board 9 E, Board 19 D): 480 on
 * a desktop, the whole screen on a phone.
 *
 * **Save is the green button** (Aaron, Oct 25, note 1.1: "In every other
 * instance that button is to save which closes the window"). The sheet's
 * fields are a draft, as every other sheet's are: **Save** writes what
 * changed in one `PATCH /activities/:id` and closes; closing with changes
 * asks *Close without saving?* (Discard · Keep editing · Save), and with none
 * just closes. Finishing the task is the circle by its title, and *Mark
 * complete* in the ⋯ menu — never the primary button; a tick saves the draft
 * with it. The footer is *Open the job* · *Text <first name>* · **Save**.
 * Done, the sheet keeps only the band, the title, who, the list, the bucket,
 * what it is attached to and its history (Board 19 D) — and the footer, since
 * who, the list and the bucket can still change.
 *
 * *Due* is New task's day picker (note 1.7) and a time only on purpose
 * (note 1.8, `DueControls`); *Notes* are rich text (note 1.4).
 *
 * Opened by an event rather than a prop, because a task opens from the
 * Calendar (a timed task's chip), the Board, the list and a deep link alike:
 * `openTaskSheet(id)` asks, and whichever `TaskSheetHost` mounted last
 * answers — the page's own, or the Tasks tab's while it is up.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  REMINDER_OPTIONS, TASK_REPEAT_OPTIONS, noteHtmlOf, taskCalendarLine, zonedInstant, zonedLocal, type ActivityPerson,
} from '@rowted/shared'
import { MenuSelect } from '../../components/menu-select.js'
import { RichNoteEditor } from '../../components/note-rich-editor.js'
import { ConfirmDialog, Popover, escapeIsFor, watchOverlayKeys } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { Face, type FacePerson } from '../faces.js'
import { openActivityNew } from '../calendar/overlays.js'
import { DoneCircle, linkGlyph } from './parts.js'
import { AttachPicker, PersonPicker } from './pickers.js'
import type { SheetPerson, TaskLink, TaskList, TaskSheetAnswer } from './types.js'
import { DueControls } from '../new/when.js'
import { TaskChatButton, TaskChatPanel, useTaskChat } from './task-chat.js'
import { PriorityField } from './task-priority.js'
import { TaskCategoriesField } from './task-categories.js'
import { NO_CHECKLIST, TaskChecklist } from './task-checklist.js'
import { useActivityNav, useBackLayer } from '../use-activity-history.js'
import { NO_TASK_FILES, TaskFiles } from './task-files.js'

/** Asks for the Task sheet (§8.2) — the calendar's timed-task chip sends the same event. */
export const TASK_OPEN_EVENT = 'rowted:activity-task'
/** Something about a task changed: lists, boards and counts reload. */
export const TASKS_CHANGED_EVENT = 'rowted:activity-tasks-changed'

export function openTaskSheet(id: string): void {
  window.dispatchEvent(new CustomEvent(TASK_OPEN_EVENT, { detail: { id } }))
}

/**
 * `source: 'list'` is the list's own circle: the list keeps that row, struck
 * through, until the view is left (Board 9 C), so it does not reload for it.
 */
export function tasksChanged(source: 'list' | null = null): void {
  window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT, { detail: { source } }))
}

/* The newest host answers; an older one (the page's) answers again once the newer goes. */
const hosts: number[] = []
let nextHost = 1

export function TaskSheetHost({ onClosed }: { onClosed?: () => void }) {
  const [id, setId] = useState<string | null>(null)
  const me = useRef(0)
  /* `?task=` opens it too — a link, a reload, a step forward (Aaron, Oct 26: the address is the one source). */
  const linked = useActivityNav().get('task')
  useEffect(() => {
    me.current = nextHost++
    hosts.push(me.current)
    const onAsk = (event: Event) => {
      if (hosts[hosts.length - 1] !== me.current) return
      setId((event as CustomEvent<{ id: string }>).detail.id)
    }
    window.addEventListener(TASK_OPEN_EVENT, onAsk)
    return () => {
      window.removeEventListener(TASK_OPEN_EVENT, onAsk)
      const at = hosts.indexOf(me.current)
      if (at >= 0) hosts.splice(at, 1)
    }
  }, [])
  useEffect(() => {
    if (linked && hosts[hosts.length - 1] === me.current) setId(linked)
  }, [linked])
  if (!id) return null
  return <TaskSheet id={id} onClose={() => { setId(null); onClosed?.() }} />
}

function Label({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center text-[12px] font-semibold text-text-muted">
      {children}
      {right && <span className="ml-auto font-normal text-text-faint">{right}</span>}
    </div>
  )
}

/** The sheet's layer — its pickers and its *Close without saving?* open above it. */
const SHEET_LAYER = 50

/**
 * What the sheet has changed and not yet saved (note 1.1). A key that is
 * present is a change, `null` included — *No date*, *no time*, *None*.
 */
export interface TaskDraft {
  title?: string
  notes?: string
  date?: string | null
  minutes?: number | null
  reminderMinutes?: number | null
  repeatRule?: string | null
  people?: SheetPerson[]
  taskList?: { id: string; name: string } | null
  bucket?: { id: string; name: string } | null
  links?: TaskLink[]
  /* Planner's extras (Aaron, Oct 25, note 1.5 — E2): saved with the rest. The checklist saves as it goes. */
  priority?: TaskSheetAnswer['priority']
  categories?: TaskSheetAnswer['categories']
}

const sameIds = (a: readonly { id: string }[], b: readonly { id: string }[]): boolean =>
  a.length === b.length && a.every((one, at) => one.id === b[at]!.id)

const linkKey = (link: { type: string; id: string }) => `${link.type}:${link.id}`

/**
 * The one `PATCH` a Save sends: only what differs from the task as it was
 * read, so a Save with nothing changed writes nothing and an audit row says
 * exactly what somebody did.
 */
export function draftPatch(
  sheet: TaskSheetAnswer, draft: TaskDraft, timeOn: boolean, timeZone: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (draft.title !== undefined && draft.title.trim() && draft.title.trim() !== sheet.title) body.title = draft.title.trim()
  if (draft.notes !== undefined && noteHtmlOf(draft.notes) !== noteHtmlOf(sheet.notes ?? '')) body.body = draft.notes
  const savedDay = sheet.dueAt ? zonedLocal(new Date(sheet.dueAt), timeZone).date : null
  const savedMinutes = sheet.dueAt && sheet.dueHasTime ? zonedLocal(new Date(sheet.dueAt), timeZone).minutes : null
  const day = draft.date !== undefined ? draft.date : savedDay
  const minutes = !day || !timeOn ? null : draft.minutes !== undefined ? draft.minutes : savedMinutes
  if (day !== savedDay || minutes !== savedMinutes) {
    if (day === null) Object.assign(body, { dueAt: null, dueHasTime: true })
    else Object.assign(body, { dueAt: zonedInstant(day, minutes ?? 12 * 60, timeZone).toISOString(), dueHasTime: minutes !== null })
  }
  if (draft.reminderMinutes !== undefined && draft.reminderMinutes !== sheet.reminderMinutes) body.reminderMinutes = draft.reminderMinutes
  if (draft.repeatRule !== undefined && draft.repeatRule !== sheet.repeatRule) body.repeatRule = draft.repeatRule
  if (draft.people !== undefined && !sameIds(draft.people, sheet.people)) body.participantIds = draft.people.map((p) => p.id)
  if (draft.taskList !== undefined && (draft.taskList?.id ?? null) !== (sheet.taskList?.id ?? null)) {
    body.taskListId = draft.taskList?.id ?? null
    body.bucketId = draft.bucket?.id ?? null
  } else if (draft.bucket !== undefined && (draft.bucket?.id ?? null) !== (sheet.bucket?.id ?? null)) {
    body.bucketId = draft.bucket?.id ?? null
  }
  if (draft.links !== undefined
    && draft.links.map(linkKey).join('|') !== sheet.links.map(linkKey).join('|')) body.links = draft.links.map(toRef)
  if (draft.priority !== undefined && draft.priority !== (sheet.priority ?? 'medium')) body.priority = draft.priority
  if (draft.categories !== undefined
    && draft.categories.map((c) => c.id).sort().join() !== (sheet.categories ?? []).map((c) => c.id).sort().join()) {
    body.categoryIds = draft.categories.map((c) => c.id)
  }
  return body
}

const toRef = (link: TaskLink): { type: 'job' | 'contact' | 'property'; id: string } => ({
  type: link.type === 'job' ? 'job' : link.type === 'property' ? 'property' : 'contact',
  id: link.id,
})

/** Bold the thing a history line names (*from **Dana Okafor’s email***, *in **Sales meeting***). */
function historyWords(words: string): ReactNode {
  const from = /^(.* made this from )(.+)$/.exec(words)
  if (from) return <>{from[1]}<b className="font-semibold text-text-body">{from[2]}</b></>
  const inList = /^(.* made this in )(.+?)( for .+)?$/.exec(words)
  if (inList) return <>{inList[1]}<b className="font-semibold text-text-body">{inList[2]}</b>{inList[3] ?? ''}</>
  /* *Tom added a file — **Front walkway.pdf*** (Aaron, Oct 26). */
  const file = /^(.* (?:added|removed) a file — )(.+)$/.exec(words)
  if (file) return <>{file[1]}<b className="font-semibold text-text-body">{file[2]}</b></>
  return words
}

export function TaskSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const phone = usePhone()
  const navigate = useNavigate()
  const { me, today, directory, reloadBadge } = useActivity()
  const timeZone = me.user.timeZone
  const [sheet, setSheet] = useState<TaskSheetAnswer | null>(null)
  const [lists, setLists] = useState<TaskList[]>([])
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<TaskDraft>({})
  /* The time box (note 1.8): open when the task has a time, closed until somebody adds one. */
  const [timeOn, setTimeOn] = useState(false)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<'person' | 'attach' | 'more' | null>(null)
  const [removing, setRemoving] = useState(false)
  /* *Close without saving?* — and what to do once it is answered (*Open the job* goes on to the job). */
  const [asking, setAsking] = useState(false)
  const then = useRef<(() => void) | null>(null)
  const panel = useRef<HTMLDivElement>(null)
  const personAt = useRef<HTMLButtonElement>(null)
  const attachAt = useRef<HTMLButtonElement>(null)
  const moreAt = useRef<HTMLButtonElement>(null)
  /* The task's chat (Aaron, Oct 25, note 1.6) — its own component, beside the sheet. */
  const chat = useTaskChat(id)

  const take = useCallback((answer: TaskSheetAnswer) => {
    setSheet(answer)
    setDraft({})
    setTimeOn(Boolean(answer.dueAt && answer.dueHasTime))
  }, [])

  const load = useCallback(() => {
    activityRequest<TaskSheetAnswer>(`/activity/tasks/${id}`)
      .then(take)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'That task could not open.'))
  }, [id, take])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    activityRequest<{ items: TaskList[] }>('/task-lists')
      .then((answer) => setLists(answer.items))
      .catch(() => setLists([]))
  }, [])

  const edit = (change: TaskDraft) => setDraft((was) => ({ ...was, ...change }))
  const changes = sheet ? draftPatch(sheet, draft, timeOn, timeZone) : {}
  const dirty = Object.keys(changes).length > 0 || (draft.title !== undefined && !draft.title.trim())

  const after = () => { tasksChanged(); reloadBadge() }

  /** Writes the draft. `false` when it could not — the sheet stays open with the reason. */
  const write = async (): Promise<boolean> => {
    if (!sheet) return false
    if (draft.title !== undefined && !draft.title.trim()) { setError('Give the task a name.'); return false }
    if (Object.keys(changes).length === 0) return true
    try {
      setBusy(true)
      setError(null)
      await activityRequest(`/activities/${id}`, { method: 'PATCH', body: changes })
      after()
      return true
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
      return false
    } finally {
      setBusy(false)
    }
  }

  /** **Save**: what changed, then the sheet closes (note 1.1) — and whatever was waiting on it. */
  const save = async () => {
    if (busy) return
    if (!await write()) return
    onClose()
    then.current?.()
    then.current = null
  }

  /** ×, Escape, the scrim, *Open the job*, back: straight away with nothing changed, else asked first (`false`). */
  const attemptClose = useCallback((next?: () => void): boolean => {
    then.current = next ?? null
    if (dirty) { setAsking(true); return false }
    onClose()
    next?.()
    then.current = null
    return true
  }, [dirty, onClose])
  /*
   * Opening a task is going somewhere: `?task=` in an entry of its own, so back closes it — and
   * with changes asks *Close without saving?* first, the sheet and its entry staying if it is
   * kept (Aaron, Oct 26). Its chat (`?chat=1`) goes with it.
   */
  useBackLayer(() => attemptClose(), { params: { task: id }, closes: ['chat'] })

  /** The circle and *Mark complete*: the draft is saved with it, so a tick never loses a change. */
  const setDone = async (done: boolean) => {
    if (!await write()) return
    try {
      setError(null)
      await activityRequest(done ? `/activities/${id}/done` : `/activities/${id}/reopen`,
        { method: 'POST', body: done ? { done: true } : {} })
      load()
      after()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
      load()
    }
  }

  useEffect(() => {
    watchOverlayKeys()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        /* Escape belongs to the top-most layer: a picker or the question closes first. */
        if (open !== null || asking || removing || !escapeIsFor(SHEET_LAYER)) return
        attemptClose()
        return
      }
      /* ⌘↵ saves from any field, as every sheet does. */
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)
        && event.target instanceof Node && panel.current?.contains(event.target)) {
        event.preventDefault()
        void save()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const remove = async () => {
    try {
      await activityRequest(`/activities/${id}`, { method: 'DELETE' })
      after()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That task could not be removed.')
      setRemoving(false)
    }
  }

  const people: ActivityPerson[] = (directory?.people ?? []).filter((p) => p.book || p.id === me.user.id)
  const byId = new Map<string, FacePerson>((directory?.people ?? []).map((p) => [p.id, p]))
  /* What the sheet shows: the draft over the task as it was read. */
  const shown = {
    title: draft.title ?? sheet?.title ?? '',
    notes: draft.notes ?? sheet?.notes ?? '',
    people: draft.people ?? sheet?.people ?? [],
    taskList: draft.taskList !== undefined ? draft.taskList : sheet?.taskList ?? null,
    bucket: draft.bucket !== undefined ? draft.bucket : sheet?.bucket ?? null,
    links: draft.links ?? sheet?.links ?? [],
    reminderMinutes: draft.reminderMinutes !== undefined ? draft.reminderMinutes : sheet?.reminderMinutes ?? null,
    repeatRule: draft.repeatRule !== undefined ? draft.repeatRule : sheet?.repeatRule ?? null,
    priority: draft.priority ?? sheet?.priority ?? 'medium',
    categories: draft.categories ?? sheet?.categories ?? [],
  }
  const list = shown.taskList ? lists.find((l) => l.id === shown.taskList!.id) ?? null : null
  const job = shown.links.find((link) => link.type === 'job') ?? null
  const contact = shown.links.find((link) => link.type === 'contact') ?? null
  const savedDay = sheet?.dueAt ? zonedLocal(new Date(sheet.dueAt), timeZone).date : null
  const savedMinutes = sheet?.dueAt && sheet.dueHasTime ? zonedLocal(new Date(sheet.dueAt), timeZone).minutes : null
  const dueDay = draft.date !== undefined ? draft.date : savedDay
  const dueMinutes = draft.minutes !== undefined ? draft.minutes : savedMinutes
  const calendarLine = sheet && !sheet.done && dueDay && timeOn && dueMinutes !== null
    ? taskCalendarLine(shown.people.map((p) => p.firstName), dueMinutes, sheet.durationMinutes ?? null)
    : null
  const toSheetPerson = (person: ActivityPerson): SheetPerson => ({
    id: person.id, name: person.name, firstName: person.firstName, initials: person.initials, colour: person.colour,
  })

  const head = (
    <header className="flex shrink-0 items-center gap-2.5 border-b border-line-faint px-4 py-3.5">
      <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-amber-band-line bg-amber-band-bg text-amber-band-text">
        <Glyph name="check_square" size={16} />
      </span>
      <div className="flex min-w-0 flex-col">
        <b className="text-[14.5px]">Task</b>
        <span className="truncate text-[11.5px] text-text-muted">{sheet?.head ?? ''}</span>
      </div>
      <span className="flex-1" />
      {sheet && <TaskChatButton chat={chat} />}
      {sheet && !sheet.done && (
        <button ref={moreAt} type="button" aria-label="More" onClick={() => setOpen('more')}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-hover">
          <Glyph name="more" size={16} />
        </button>
      )}
      <button type="button" aria-label="Close" onClick={() => attemptClose()}
        className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-hover">
        <Glyph name="close" size={16} />
      </button>
    </header>
  )

  const assigned = sheet && (
    <div className="col-span-2">
      <Label>Assigned to</Label>
      <div className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-lg border border-line bg-surface px-1.5 py-1">
        {shown.people.map((person) => (
          <span key={person.id} className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-surface pl-[3px] pr-1.5 text-[12px] font-semibold">
            <Face person={byId.get(person.id) ?? person} size={22} />
            {person.name}
            <button type="button" aria-label={`Take ${person.name} off`} className="text-text-muted hover:text-text"
              onClick={() => edit({ people: shown.people.filter((p) => p.id !== person.id) })}>
              <Glyph name="close" size={12} />
            </button>
          </span>
        ))}
        <button ref={personAt} type="button" onClick={() => setOpen('person')}
          className="inline-flex h-7 items-center gap-1 px-1.5 text-[12px] font-semibold text-accent">
          <Glyph name="plus" size={13} />Add a person
        </button>
      </div>
    </div>
  )

  const listField = sheet && (
    <div>
      <Label>Task list</Label>
      <MenuSelect
        label="Task list" size="field" noSearch value={shown.taskList?.id ?? null}
        placeholder="None" clearable clearLabel="None"
        /* The sheet's own list until the lists arrive, so it never reads *None* for a moment (Board 19 D). */
        options={[...lists.map((l) => ({ value: l.id, label: l.name, glyph: <Glyph name="board" size={14} /> })),
          ...(shown.taskList && !lists.some((l) => l.id === shown.taskList!.id)
            ? [{ value: shown.taskList.id, label: shown.taskList.name, glyph: <Glyph name="board" size={14} /> }] : [])]}
        onChange={(value) => {
          const next = value ? lists.find((l) => l.id === value) ?? null : null
          const first = next?.buckets[0] ?? null
          edit({ taskList: next ? { id: next.id, name: next.name } : null, bucket: first ? { id: first.id, name: first.name } : null })
        }}
        className="w-full"
      />
    </div>
  )

  const bucketField = sheet && (list || shown.bucket) && (
    <div>
      <Label>Bucket</Label>
      <MenuSelect
        label="Bucket" size="field" noSearch value={shown.bucket?.id ?? null}
        options={list ? list.buckets.map((b) => ({ value: b.id, label: b.name })) : [{ value: shown.bucket!.id, label: shown.bucket!.name }]}
        onChange={(value) => {
          const next = list?.buckets.find((b) => b.id === value)
          if (next) edit({ bucket: { id: next.id, name: next.name } })
        }}
        className="w-full"
      />
    </div>
  )

  const attached = sheet && (
    <div>
      <Label right={sheet.done || shown.links.length === 0 ? 'optional' : undefined}>Attached to</Label>
      {shown.links.length === 0 ? (
        <button ref={attachAt} type="button" disabled={sheet.done} onClick={() => setOpen('attach')}
          className="flex min-h-[38px] w-full items-center rounded-lg border border-line bg-surface px-2.5 text-left text-[13px] text-text-faint">
          Nothing — a task for the team
        </button>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {shown.links.map((link) => (
              <span key={`${link.type}:${link.id}`} className="inline-flex h-7 items-center gap-[5px] rounded-lg border border-accent-line bg-green-band-bg pl-[9px] pr-1.5 text-[12px] font-semibold">
                <Glyph name={linkGlyph(link.type)} size={12} />
                {link.label}
                {!sheet.done && (
                  <button type="button" aria-label={`Take ${link.label} off`} className="text-text-muted hover:text-text"
                    onClick={() => edit({ links: shown.links.filter((l) => l !== link) })}>
                    <Glyph name="close" size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
          {!sheet.done && (
            <button ref={attachAt} type="button" onClick={() => setOpen('attach')}
              className="mt-1.5 inline-flex h-7 items-center gap-1 px-2 text-[12px] font-semibold text-accent">
              <Glyph name="plus" size={13} />Add
            </button>
          )}
        </>
      )}
    </div>
  )

  const history = sheet && sheet.history.length > 0 && (
    <div>
      <Label>History</Label>
      <div className={cn('text-[12px] text-text-muted', sheet.done ? 'leading-[1.8]' : 'leading-[1.7]')}>
        {sheet.history.map((entry, index) => {
          const first = entry.words.split(' ')[0] ?? ''
          const who = (directory?.people ?? []).find((p) => p.firstName === first)
          return (
            <div key={`${entry.at}-${index}`} className="flex items-center gap-1.5">
              {who && <Face person={who} size={18} />}
              <span>{historyWords(entry.words)} · {entry.when}</span>
            </div>
          )
        })}
      </div>
    </div>
  )

  const body = !sheet ? (
    <div className="p-4 text-[13px] text-text-muted">{error ?? 'Loading…'}</div>
  ) : sheet.done ? (
    <div className="flex flex-col p-4">
      {sheet.doneBand && (
        <div className="flex items-center gap-2.5 rounded-[10px] border border-accent-line bg-green-band-bg px-3 py-2.5 text-[12.5px] text-accent-dark">
          <DoneCircle done size={20} />
          <span><b>{sheet.doneBand.by}</b> {sheet.doneBand.rest}</span>
          <span className="flex-1" />
          <button type="button" disabled={busy} onClick={() => void setDone(false)} className="font-bold text-accent">Reopen</button>
        </div>
      )}
      <b className="mt-3.5 text-[17px] text-text-faint line-through">{sheet.title}</b>
      <div className="mt-3.5 grid grid-cols-2 gap-3">
        {assigned}
        {listField}
        {bucketField}
      </div>
      {shown.categories.length > 0 && (
        <div className="mt-3">
          <Label>Categories</Label>
          <TaskCategoriesField value={shown.categories} onChange={() => undefined} readOnly
            listId={shown.taskList?.id ?? null} taskId={id} />
        </div>
      )}
      <div className="mt-3">{attached}</div>
      {(sheet.checklist?.items.length ?? 0) > 0 && (
        <div className="mt-3"><TaskChecklist taskId={id} initial={sheet.checklist} people={sheet.people} readOnly /></div>
      )}
      {(sheet.files?.length ?? 0) > 0 && (
        <div className="mt-3"><TaskFiles taskId={id} initial={sheet.files ?? NO_TASK_FILES} readOnly /></div>
      )}
      <div className="mt-3">{history}</div>
      {/* The done view has a Save too now (note 1.1), so it says why one did not go. */}
      {error && <div className="mt-3 text-[12.5px] text-danger">{error}</div>}
    </div>
  ) : (
    <div className="flex flex-col p-4">
      <div className="flex items-start gap-2.5">
        <span className="pt-0.5">
          <DoneCircle done={false} late={sheet.due?.tone === 'late'} size={22} label="Mark complete"
            onToggle={() => void setDone(true)} />
        </span>
        <input
          aria-label="Task"
          value={shown.title}
          onChange={(event) => edit({ title: event.target.value })}
          onKeyDown={(event) => { if (event.key === 'Enter') (event.target as HTMLInputElement).blur() }}
          className="min-w-0 flex-1 rounded-md bg-transparent text-[17px] font-bold leading-[1.3] text-text outline-none focus:bg-field"
        />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <Label>Due</Label>
          <DueControls date={dueDay} time={dueMinutes} timeOn={timeOn} phone={phone} dense sayToday today={today}
            placeholder="No date"
            onDate={(day) => edit({ date: day })} onClear={() => { edit({ date: null }); setTimeOn(false) }}
            onTime={(minutes) => edit({ minutes })} onTimeOn={setTimeOn} />
        </div>
        <div>
          <Label>Reminder</Label>
          <MenuSelect
            label="Reminder" size="field" noSearch
            value={shown.reminderMinutes === null ? 'none' : String(shown.reminderMinutes)}
            options={REMINDER_OPTIONS.map((o) => ({ value: o.minutes === null ? 'none' : String(o.minutes), label: o.label }))}
            onChange={(value) => edit({ reminderMinutes: !value || value === 'none' ? null : Number(value) })}
            className="w-full"
          />
        </div>
        {assigned}
        <div>
          <Label>Repeats</Label>
          <MenuSelect
            label="Repeats" size="field" noSearch value={shown.repeatRule ?? 'never'}
            options={TASK_REPEAT_OPTIONS.map((o) => ({ value: o.rule ?? 'never', label: o.label }))}
            onChange={(value) => edit({ repeatRule: !value || value === 'never' ? null : value })}
            className="w-full"
          />
        </div>
        {listField}
        {bucketField}
        <div>
          <Label>Priority</Label>
          <PriorityField value={shown.priority} onChange={(priority) => edit({ priority })} />
        </div>
      </div>
      {calendarLine && (
        <div className="mt-2.5 flex items-center gap-2 rounded-[9px] bg-surface-alt px-2.5 py-2 text-[12px] text-text-muted">
          <span className="text-warn"><Glyph name="calendar" size={13} /></span>
          {calendarLine}
        </div>
      )}
      <div className="mt-4">
        <Label>Categories</Label>
        {/* The task's list's own set, or the company's for a task on no list (Aaron, Oct 26, answer 1). */}
        <TaskCategoriesField value={shown.categories} onChange={(categories) => edit({ categories })}
          listId={shown.taskList?.id ?? null} taskId={id} />
      </div>
      <div className="mt-4">{attached}</div>
      <div className="mt-4">
        <Label>Notes</Label>
        <RichNoteEditor label="Notes" value={shown.notes} onChange={(html) => edit({ notes: html })} phone={phone}
          minHeight={70} layer={70} />
      </div>
      <div className="mt-4">
        <TaskChecklist taskId={id} initial={sheet.checklist ?? NO_CHECKLIST} people={sheet.people} onChanged={after} />
      </div>
      {/* Planner's attachments (Aaron, Oct 26): under the checklist, the first few and *Show N more*. */}
      <div className="mt-4">
        <TaskFiles taskId={id} initial={sheet.files ?? NO_TASK_FILES} />
      </div>
      <div className="mt-4">{history}</div>
      {error && <div className="mt-3 text-[12.5px] text-danger">{error}</div>}
    </div>
  )

  const footer = sheet && (
    <footer className="flex shrink-0 items-center gap-2 border-t border-line bg-surface-alt px-4 py-3">
      {job && (
        <button type="button" onClick={() => attemptClose(() => navigate(`/jobs/${job.id}`))}
          className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-line bg-surface px-3.5 text-[12.5px] font-semibold hover:bg-hover">
          <Glyph name="layers" size={14} />Open the job
        </button>
      )}
      {contact && (
        <button type="button"
          onClick={() => openActivityNew({ kind: 'text', prefill: { contactId: contact.id } })}
          className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-line bg-surface px-3.5 text-[12.5px] font-semibold hover:bg-hover">
          <Glyph name="bubble" size={14} />Text {contact.label.split(' ')[0]}
        </button>
      )}
      <span className="flex-1" />
      {/* Save, the green button every other sheet has (note 1.1): never greyed — with nothing changed it just closes. */}
      <button type="button" disabled={busy} onClick={() => void save()}
        className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark disabled:opacity-60">
        <Glyph name="check" size={14} stroke={2.4} />{busy ? 'Saving…' : 'Save'}
      </button>
    </footer>
  )

  return createPortal(
    <div data-overlay-layer={SHEET_LAYER} className="fixed inset-0 z-50 flex justify-end bg-[rgba(29,29,26,.18)]"
      onMouseDown={(event) => { if (event.target === event.currentTarget) attemptClose() }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label="Task"
        className={cn('flex h-full flex-col border-l border-line bg-surface shadow-slideover', phone ? 'w-full' : 'w-[480px] max-w-full')}>
        {head}
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
        {footer}
      </div>
      {sheet && chat.open && <TaskChatPanel chat={chat} onCloseSheet={() => attemptClose()} />}
      {sheet && (
        <>
          <PersonPicker open={open === 'person'} onClose={() => setOpen(null)} anchor={personAt.current} people={people}
            taken={shown.people.map((p) => p.id)}
            onPick={(person) => edit({ people: [...shown.people, toSheetPerson(person)] })} />
          <AttachPicker open={open === 'attach'} onClose={() => setOpen(null)} anchor={attachAt.current}
            onPick={(hit) => {
              if (shown.links.some((l) => l.type === hit.type && l.id === hit.id)) return
              edit({ links: [...shown.links, { type: hit.type, id: hit.id, label: hit.label }] })
            }} />
          <MoreMenu open={open === 'more'} anchor={moreAt.current} onClose={() => setOpen(null)}
            onComplete={() => { setOpen(null); void setDone(true) }}
            onRemove={() => { setOpen(null); setRemoving(true) }} />
        </>
      )}
      {asking && sheet && (
        <ConfirmDialog
          title="Close without saving?"
          body={`“${shown.title.trim() || sheet.title}” has changes that will be lost. Keep editing, or throw them away.`}
          confirmLabel="Save"
          cancelLabel="Keep editing"
          tone="default"
          layer={70}
          extra={(
            <button type="button" onClick={() => {
              setAsking(false)
              onClose()
              then.current?.()
              then.current = null
            }}
              className="mr-auto text-body font-semibold text-danger hover:underline">
              Discard changes
            </button>
          )}
          onConfirm={() => { setAsking(false); void save() }}
          onCancel={() => { setAsking(false); then.current = null }}
        />
      )}
      {removing && sheet && (
        <ConfirmDialog
          title={`Remove “${sheet.title}”?`}
          body="It comes off every list and calendar it is on. Its history stays."
          confirmLabel="Remove"
          tone="danger"
          layer={70}
          onCancel={() => setRemoving(false)}
          onConfirm={() => void remove()}
        />
      )}
    </div>,
    document.body,
  )
}

function MoreMenu({ open, anchor, onClose, onComplete, onRemove }: {
  open: boolean; anchor: HTMLElement | null; onClose: () => void; onComplete: () => void; onRemove: () => void
}) {
  const phone = usePhone()
  if (!open) return null
  return (
    <Popover open onClose={onClose} anchor={anchor} width={200} align="right" label="More" className="rounded-xl py-1" layer={70}>
      {/* Finishing it, said plainly, now that the green button saves (note 1.1). */}
      <button type="button" onClick={onComplete}
        className={cn('flex w-full items-center gap-2 px-3 text-left text-[13px] font-medium text-text hover:bg-hover',
          phone ? 'h-[52px]' : 'h-9')}>
        <Glyph name="check" size={14} />Mark complete
      </button>
      <button type="button" onClick={onRemove}
        className={cn('flex w-full items-center gap-2 px-3 text-left text-[13px] font-medium text-danger hover:bg-hover',
          phone ? 'h-[52px]' : 'h-9')}>
        <Glyph name="close" size={14} />Remove task
      </button>
    </Popover>
  )
}
